import React from 'react';
import { IconButton, Tooltip, Paper } from '@mui/material';
import {
  GridValueFormatterParams,
  GridRenderCellParams,
  GridValueGetterParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { Copy, User as PersonIcon } from 'react-feather';
import moment from 'moment';
import { FullScreen } from '@pv/common/components';
import { phonePrint } from '@pv/common/utils';
import { enqueueSnackbar } from 'notistack';
import copyToClipboard from 'copy-to-clipboard';
import { ADMIN_USERS_QUERY } from './graphql';
import { AdminReport } from '../reports';

export const UsersTable = () => {
  const navigate = useNavigate();

  const allColumns = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: true,
    },
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.organization?.name,
    },
    {
      field: 'venues',
      headerName: 'Venues',
      flex: 1,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.venues
          ?.map((v: any) => v.name)
          .filter(Boolean)
          .join(', ') || '',
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1,
      sortable: true,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 1,
      sortable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      sortable: true,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value ? phonePrint(params.value) : '-',
    },
    {
      field: 'productUpdateEmail',
      headerName: 'Product Update Email',
      width: 200,
      type: 'boolean',
      sortable: false,
    },
    {
      field: 'lastSeenAt',
      headerName: 'Last Seen',
      width: 180,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value ? moment(params.value).format('MM/DD/YY h:mm:ss a') : '-',
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'copyMagicLink',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <Tooltip title="Copy Magic Link">
            <IconButton
              size="small"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (params.row.magicLoginLink) {
                  copyToClipboard(params.row.magicLoginLink);
                  enqueueSnackbar(`Copied magic link for ${params.row.email}`, {
                    variant: 'info',
                  });
                }
              }}
              color="primary"
            >
              <Copy size={16} />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
    {
      field: 'loginAsUser',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Login as User">
          <IconButton
            size="small"
            href={params.row.magicLoginLink}
            color="primary"
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
            }}
          >
            <PersonIcon size={16} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <FullScreen paddingBottom={24}>
        {({ height }) => (
          <Paper sx={{ height, width: '100%' }}>
            <AdminReport
              reportName="adminUsers"
              columns={allColumns}
              title="Users"
              toolbar={true}
              query={ADMIN_USERS_QUERY}
              onRowClick={(params: GridRowParams) => {
                navigate(`/admin/users/${params.row.id}`);
              }}
            />
          </Paper>
        )}
      </FullScreen>
    </>
  );
};

export default UsersTable;
